/** Staff board: every CRM + Marketing member's brief (budget / areas / goals), status and notes. */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, Loader2, Megaphone, Pencil, Phone, Mail, Save, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { wget, wpatch } from '@/components/website/api';
import { EMPTY_ANSWERS, OnboardingQuestions } from '@/components/marketing/OnboardingQuestions';

const STATUS = {
  awaiting: { label: 'Awaiting answers', cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: Clock },
  submitted: { label: 'Brief received', cls: 'bg-sky-100 text-sky-800 border-sky-200', Icon: Megaphone },
  in_progress: { label: 'Setting up', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200', Icon: Wrench },
  set_up: { label: 'Live', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: CheckCircle2 },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

function Badge({ s }) {
  const m = STATUS[s] || STATUS.awaiting;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${m.cls}`} data-testid={`mo-status-${s}`}><m.Icon className="w-3 h-3" /> {m.label}</span>;
}

function Detail({ row, options, onSaved }) {
  const [notes, setNotes] = useState(row.staff_notes || '');
  const [fill, setFill] = useState(false);
  const [a, setA] = useState({ ...EMPTY_ANSWERS, ...(row.answers || {}) });
  const [busy, setBusy] = useState('');
  useEffect(() => { setNotes(row.staff_notes || ''); setA({ ...EMPTY_ANSWERS, ...(row.answers || {}) }); setFill(false); }, [row]);
  const patch = async (body, label) => {
    setBusy(label);
    try { const d = await wpatch(`/staff/marketing-onboarding/${row.user_id}`, body); onSaved(d.doc); toast.success(label); setFill(false); }
    catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };
  const u = row.user;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5" data-testid="mo-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{u.company_name || u.name}</h2>
          <div className="text-xs text-slate-500">{u.name}{u.postcode ? ` · ${u.postcode}` : ''}{row.submitted_at ? ` · brief ${fmt(row.submitted_at)}` : ` · joined ${fmt(row.created_at)}`}{row.submitted_by_staff ? ` · filled in by ${row.submitted_by_staff}` : ''}</div>
          <div className="flex flex-wrap gap-3 text-xs mt-1.5">
            {u.phone && <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1 text-[#006400] font-semibold" data-testid="mo-detail-phone"><Phone className="w-3 h-3" /> {u.phone}</a>}
            {u.email && <a href={`mailto:${u.email}`} className="inline-flex items-center gap-1 text-[#006400] font-semibold" data-testid="mo-detail-email"><Mail className="w-3 h-3" /> {u.email}</a>}
          </div>
        </div>
        <Badge s={row.status} />
      </div>

      {row.submitted_at && !fill ? (
        <ul className="space-y-1.5 text-sm text-slate-800 rounded-xl bg-slate-50 border border-slate-200 p-4" data-testid="mo-detail-summary">
          {row.summary.map((l) => <li key={l} className="flex gap-2"><span className="text-[#006400]">•</span><span>{l}</span></li>)}
        </ul>
      ) : !fill && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900" data-testid="mo-detail-awaiting">
          No brief yet{row.reminder_sent_at ? ` — reminder sent ${fmt(row.reminder_sent_at)}` : ''}. Ring them and fill it in below, or wait for them to complete it from their Home page.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setFill((f) => !f)} data-testid="mo-detail-fill"><Pencil className="w-3.5 h-3.5 mr-1" /> {fill ? 'Hide form' : row.submitted_at ? 'Edit answers' : 'Fill in on their behalf'}</Button>
        {row.status !== 'in_progress' && row.status !== 'set_up' && <Button size="sm" onClick={() => patch({ status: 'in_progress' }, 'Marked as setting up')} disabled={!!busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="mo-detail-in-progress"><Wrench className="w-3.5 h-3.5 mr-1" /> Start setting up</Button>}
        {row.status !== 'set_up' && <Button size="sm" onClick={() => patch({ status: 'set_up' }, 'Marked as live — member notified')} disabled={!!busy} className="bg-[#006400] hover:bg-[#004d00] text-white" data-testid="mo-detail-set-up"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark set up</Button>}
        {row.status === 'set_up' && <Button size="sm" variant="outline" onClick={() => patch({ status: 'in_progress' }, 'Reopened')} disabled={!!busy} data-testid="mo-detail-reopen">Reopen</Button>}
      </div>

      {fill && (
        <div className="space-y-3">
          <OnboardingQuestions a={a} set={(p) => setA((prev) => ({ ...prev, ...p }))} options={options} prefill={{ postcode: u.postcode }} />
          <div className="flex justify-end"><Button onClick={() => patch({ answers: a }, 'Answers saved')} disabled={busy === 'Answers saved'} className="bg-[#006400] hover:bg-[#004d00] text-white" data-testid="mo-detail-save-answers"><Save className="w-4 h-4 mr-1" /> Save answers</Button></div>
        </div>
      )}

      <div>
        <div className="text-xs font-bold text-slate-700 mb-1.5">Staff notes</div>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call notes, ad account IDs, what's been set up…" className="min-h-[90px]" data-testid="mo-detail-notes" />
        <div className="flex justify-end mt-2"><Button size="sm" variant="outline" onClick={() => patch({ staff_notes: notes }, 'Notes saved')} disabled={busy === 'Notes saved' || notes === (row.staff_notes || '')} data-testid="mo-detail-save-notes"><Save className="w-3.5 h-3.5 mr-1" /> Save notes</Button></div>
      </div>
    </div>
  );
}

export default function StaffMarketingOnboarding() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState(null);
  const load = () => wget('/staff/marketing-onboarding').then(setData).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => (data?.rows || []).filter((r) => filter === 'all' || r.status === filter), [data, filter]);
  const selected = (data?.rows || []).find((r) => r.user_id === sel) || rows[0];
  const onSaved = (doc) => { setSel(doc.user_id); load(); };

  if (!data) return <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-700" /></div>;
  return (
    <div className="min-h-screen bg-[#E8ECEF]" data-testid="staff-marketing-onboarding">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button type="button" onClick={() => navigate('/staff')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-3" data-testid="mo-staff-back"><ArrowLeft className="w-4 h-4" /> Staff dashboard</button>
        <div className="flex items-start gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-teal-600 text-white flex items-center justify-center"><Megaphone className="w-5 h-5" /></div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Marketing onboarding</h1>
            <p className="text-sm text-slate-600">Every CRM + Marketing member — their budget, areas and goals, and where their set-up is at.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="mo-filters">
          {[['all', 'All', data.rows.length], ...Object.keys(STATUS).map((s) => [s, STATUS[s].label, data.counts[s] || 0])].map(([k, label, n]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`mo-filter-${k}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${filter === k ? 'bg-teal-700 border-teal-700 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-teal-500'}`}>{label} <span className="opacity-70">{n}</span></button>
          ))}
        </div>
        {!rows.length ? <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="mo-empty">No CRM + Marketing members {filter === 'all' ? 'yet' : 'in this state'}.</div> : (
          <div className="grid lg:grid-cols-[360px_1fr] gap-4 items-start">
            <ul className="bg-white rounded-2xl border border-slate-200 divide-y overflow-hidden" data-testid="mo-list">
              {rows.map((r) => (
                <li key={r.user_id}>
                  <button type="button" onClick={() => setSel(r.user_id)} data-testid={`mo-row-${r.user_id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-teal-50/60 ${selected?.user_id === r.user_id ? 'bg-teal-50 border-l-4 border-teal-600' : 'border-l-4 border-transparent'}`}>
                    <div className="flex items-center justify-between gap-2"><span className="font-bold text-sm text-slate-900 truncate">{r.user.company_name || r.user.name}</span><Badge s={r.status} /></div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{r.submitted_at ? r.summary[0] : `Joined ${fmt(r.created_at)}`}{!r.still_member ? ' · no longer on Marketing' : ''}</div>
                  </button>
                </li>
              ))}
            </ul>
            {selected && <Detail row={selected} options={data.options} onSaved={onSaved} />}
          </div>
        )}
      </div>
    </div>
  );
}
